/**
 * Lista inline de anuncios tipo `card`.
 *
 * A diferencia del modal y banner (globales en Layout), las cards se renderizan
 * inline encima del contenido de la página. Layout monta este componente arriba
 * del <Outlet />, lo que las hace aparecer en cualquier ruta del panel sin
 * cambiar la estructura interna de cada página.
 *
 * Comparte el fetch global con CommunicationsCenter — ambos consumen el mismo
 * useGetAnnouncement. La caché vive en el hook (módulo de servicios de axios),
 * así que dos consumidores no disparan dos GETs simultáneos en el mismo render.
 */

import { useMemo, useState } from 'react';
import { Button } from '@plick/ui';
import { useGetAnnouncement } from './CommunicationsModal/hooks/useGetAnnouncement';
import { CommunicationsCard } from './CommunicationsCard/CommunicationsCard';
import { useDismissedAnnouncements } from './shared/useDismissedAnnouncements';

const PREVIEW_LIMIT = 3;

export const AnnouncementCardList = () => {
  const { announcements, isFetching } = useGetAnnouncement();
  const { isDismissed, dismiss } = useDismissedAnnouncements();
  const [expanded, setExpanded] = useState(false);

  const cards = useMemo(
    () => announcements.filter((a) => a.announcement_type === 'card' && !isDismissed(a.uuid)),
    [announcements, isDismissed],
  );

  if (isFetching || cards.length === 0) return null;

  const visible = expanded ? cards : cards.slice(0, PREVIEW_LIMIT);
  const hasMore = cards.length > PREVIEW_LIMIT;

  return (
    <section aria-label="Anuncios" className="w-full px-3 sm:px-4 pt-3 flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((card) => (
          <CommunicationsCard key={card.uuid} announcement={card} onClose={() => dismiss(card.uuid)} />
        ))}
      </div>

      {hasMore && (
        <div className="self-center">
          <Button.Secondary
            text={expanded ? 'Mostrar menos' : `Ver ${cards.length - PREVIEW_LIMIT} más`}
            onClick={() => setExpanded((v) => !v)}
          />
        </div>
      )}
    </section>
  );
};
